import React, { FC, PropsWithChildren } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Typography } from '@mui/material'

import { AvailableTestParam, PossibleValue, ParamType } from 'src/Libs/installation-details/dto/hardware-test-result.dto'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { NumberField, DatePicker, DateTimePicker } from 'src/Libs/inputs/formik'
import Select from 'src/Libs/inputs/formik/Select'
import BooleanToggle from 'src/Libs/inputs/formik/BooleanToggle'
import TextField from 'src/Libs/inputs/formik/TextField'
import { LabeledOption } from '@e3dc-react/shell/Types/globalTypes'
import { getHardwareTestMessage, getTranslatedDefaultValue } from 'src/Libs/installation-details/utils'
import { MessageData } from '../../../../models/hardware-tests.model'

const useStyles = makeStyles()(theme => ({
  formItem: {
    width: '100%',
    marginBottom: theme.spacing(5),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  textLabel: {
    marginBottom: theme.spacing(2),
  },
}))

interface FormItemProps extends PropsWithChildren {
  description: MessageData
}

const FormItem: FC<FormItemProps> = ({ children, description }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  return (
    <Box className={classes.formItem}>
      <Typography className={classes.textLabel} variant="body2">
        {getHardwareTestMessage(description, t)}
      </Typography>
      {children}
    </Box>
  )
}

interface TestParametersFormProps {
  paramData: AvailableTestParam
}

/**
 * Returns relevant input component based on parameter type
 */
const ParamInput: FC<TestParametersFormProps> = ({ paramData }) => {
  const { description, key, name, paramType, max, min, possibleValues } = paramData
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  const mapSelectOptions = (possibleValues?: PossibleValue[]): LabeledOption<PossibleValue>[] => {
    if (possibleValues) {
      return possibleValues.map(value => {
        if (typeof value === 'object') {
          return { label: getHardwareTestMessage(value, t), value: getTranslatedDefaultValue(value, t) }
        }

        return { label: value.toString(), value }
      })
    }

    return []
  }

  const mapToggleOptions = (possibleValues?: PossibleValue[]): string[] => {
    if (possibleValues) {
      return possibleValues.map(value => {
        if (typeof value === 'object') {
          return getHardwareTestMessage(value, t)
        }
        return `${value}`
      })
    }

    return []
  }

  switch (paramType) {
    case ParamType.ENUM: {
      // Param with enum type and only two possible values is boolean type
      if ((possibleValues?.length ?? 0) === 2) {
        return (
          <FormItem description={description}>
            <BooleanToggle trueLabel={mapToggleOptions(possibleValues)[0]} falseLabel={mapToggleOptions(possibleValues)[1]} name={key} />
          </FormItem>
        )
      }
      return (
        <FormItem description={description}>
          <Select label={getHardwareTestMessage(name, t)} name={key} options={mapSelectOptions(possibleValues)} />
        </FormItem>
      )
    }
    case ParamType.NUMBER:
    case ParamType.NUMBERRANGE: {
      return (
        <FormItem description={description}>
          <NumberField
            className={classes.formItem}
            key={key}
            label={getHardwareTestMessage(name, t)}
            name={key}
            min={min}
            max={max}
            textAlign="left"
            fullWidth
            ignoreDefaultValue
          />
        </FormItem>
      )
    }
    case ParamType.DATETIME: {
      return (
        <FormItem description={description}>
          <DateTimePicker className={classes.formItem} key={key} label={getHardwareTestMessage(name, t)} name={key} />
        </FormItem>
      )
    }
    case ParamType.DATE: {
      return (
        <FormItem description={description}>
          <DatePicker className={classes.formItem} key={key} label={getHardwareTestMessage(name, t)} name={key} />
        </FormItem>
      )
    }
    // For text case and unknown types of parameters
    default: {
      return (
        <FormItem description={description}>
          <TextField className={classes.formItem} key={key} label={getHardwareTestMessage(name, t)} name={key} fullWidth />
        </FormItem>
      )
    }
  }
}

export default ParamInput
